import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeftRight,
  Ban,
  Boxes,
  Check,
  FileText,
  Landmark,
  Loader2,
  Paperclip,
  RotateCcw,
  Trash2,
  Undo2,
  Upload,
} from 'lucide-react';
import { AttachmentChip } from '@/components/attachment-chip';
import { AttachmentViewer, type ViewableFile } from '@/components/attachment-viewer';
import { Badge } from '@/components/badge';
import { Button } from '@/components/button';
import { Input } from '@/components/input';
import { Card, CardBody } from '@/components/card';
import { DataList, DataListRow } from '@/components/data-list';
import { EmptyState } from '@/components/empty-state';
import { Select } from '@/components/select';
import { SkeletonList } from '@/components/skeleton';
import { useToast } from '@/components/toast';
import {
  currentPeriod,
  LedgerToolbar,
  PeriodFilter,
  periodRange,
  SearchBox,
  SortMenu,
  type Period,
  type SortOption,
} from '@/components/ledger-toolbar';
import { useAccounts } from '@/lib/accounting';
import { useDebouncedValue } from '@/lib/search';
import {
  useBankConnections,
  useBankTransactions,
  useDeleteTransactionAttachment,
  useIgnoreTransaction,
  usePostTransaction,
  useRefundCandidates,
  useRefundTransaction,
  useTransferTransaction,
  useUnpostTransaction,
  useUploadTransactionAttachment,
} from '@/lib/banking';
import { dollars, formatBytes, formatDateFull } from '@/lib/format';
import {
  useAssetClasses,
  useCapitalizeTransaction,
  useFixedAssets,
} from '@/lib/fixed-assets';
import type { AccountData, AssetClass, BankTransactionData } from '@/types/api';

const STATUS_FILTERS = [
  { key: 'unreviewed', label: 'To review' },
  { key: 'posted', label: 'Posted' },
  { key: 'ignored', label: 'Ignored' },
  { key: '', label: 'All' },
] as const;

const SORT_OPTIONS: SortOption[] = [
  { value: 'date_desc', label: 'Newest first' },
  { value: 'date_asc', label: 'Oldest first' },
  { value: 'amount_desc', label: 'Amount: high → low' },
  { value: 'amount_asc', label: 'Amount: low → high' },
];

export function TransactionsTab() {
  const [searchParams] = useSearchParams();
  const focusId = searchParams.get('focus');
  const [status, setStatus] = useState<string>(() => {
    const s = searchParams.get('status');
    return s != null && STATUS_FILTERS.some((f) => f.key === s) ? s : 'unreviewed';
  });
  const [search, setSearch] = useState('');
  const [period, setPeriod] = useState<Period>(currentPeriod);
  const [sort, setSort] = useState('date_desc');
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebouncedValue(search, 300);
  const range = periodRange(period);
  const { data, isLoading } = useBankTransactions({
    status: status || undefined,
    q: debouncedSearch || undefined,
    from: range.from,
    to: range.to,
    sort,
    page,
  });
  const accounts = useAccounts();
  const connections = useBankConnections();

  // Flat list of every linked bank account, for the transfer picker. `mapped`
  // gates whether it can be a transfer target (both sides need a ledger account).
  const bankAccounts = useMemo<TransferableAccount[]>(
    () =>
      (connections.data ?? []).flatMap((c) =>
        c.accounts.map((a) => ({
          id: a.id,
          label: a.name + (a.mask ? ` ····${a.mask}` : ''),
          mapped: a.posted_to_account_id != null,
        })),
      ),
    [connections.data],
  );

  // Any filter change should return to the first page.
  function resetTo<T>(setter: (v: T) => void) {
    return (v: T) => {
      setter(v);
      setPage(1);
    };
  }

  const rows = data?.data ?? [];
  const meta = data?.meta;

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold">Transactions</h3>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Review imported bank activity and post each item to the ledger. Sync a bank from the Bank
          tab to pull the latest.
        </p>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.key || 'all'}
            type="button"
            onClick={() => resetTo(setStatus)(f.key)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              status === f.key
                ? 'bg-foreground text-background'
                : 'text-muted-foreground hover:bg-muted'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <LedgerToolbar>
        <SearchBox
          value={search}
          onChange={resetTo(setSearch)}
          placeholder="Search merchant or description…"
        />
        <div className="flex items-center gap-2">
          <PeriodFilter value={period} onChange={resetTo(setPeriod)} />
          <SortMenu options={SORT_OPTIONS} value={sort} onChange={resetTo(setSort)} />
        </div>
      </LedgerToolbar>

      <Card>
        <CardBody>
          {isLoading ? (
            <SkeletonList rows={5} />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={Landmark}
              title="Nothing here"
              description={
                status === 'unreviewed'
                  ? 'No transactions waiting for review. Sync a bank to import the latest activity.'
                  : 'No transactions match this filter.'
              }
            />
          ) : (
            <DataList>
              {rows.map((t) => (
                <TransactionRow
                  key={t.id}
                  txn={t}
                  accounts={accounts.data ?? []}
                  bankAccounts={bankAccounts}
                  focused={focusId === String(t.id)}
                />
              ))}
            </DataList>
          )}
        </CardBody>
      </Card>

      {meta && meta.last_page > 1 ? (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            Page {meta.current_page} of {meta.last_page} · {meta.total} total
          </span>
          <div className="flex gap-1.5">
            <Button
              variant="secondary"
              disabled={meta.current_page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              disabled={meta.current_page >= meta.last_page}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

type TransferableAccount = { id: number; label: string; mapped: boolean };

// Sentinel select values that switch the review control between categorizing,
// recording an inter-account transfer, and matching a refund to a purchase.
const TRANSFER_OPTION = '__transfer__';
const REFUND_OPTION = '__refund__';
const CATEGORY_OPTION = '__category__';
const CAPITALIZE_OPTION = '__capitalize__';
const NEW_ASSET_OPTION = '__new_asset__';

function TransactionRow({
  txn,
  accounts,
  bankAccounts,
  focused = false,
}: {
  txn: BankTransactionData;
  accounts: AccountData[];
  bankAccounts: TransferableAccount[];
  focused?: boolean;
}) {
  const post = usePostTransaction();
  const transfer = useTransferTransaction();
  const refund = useRefundTransaction();
  const ignore = useIgnoreTransaction();
  const unpost = useUnpostTransaction();
  const toast = useToast();
  const navigate = useNavigate();
  const [accountId, setAccountId] = useState<number | ''>(txn.suggested_account_id ?? '');
  // Other linked accounts this one could transfer with.
  const otherAccounts = useMemo(
    () => bankAccounts.filter((a) => a.id !== txn.bank_account_id),
    [bankAccounts, txn.bank_account_id],
  );
  const inflow = txn.amount_cents > 0;
  // A sync-detected opposite leg / matching purchase defaults the row's mode.
  const [mode, setMode] = useState<'category' | 'transfer' | 'refund' | 'capitalize'>(
    txn.transfer_candidate_bank_account_id != null
      ? 'transfer'
      : txn.refund_candidate_bank_transaction_id != null
        ? 'refund'
        : 'category',
  );
  const [counterpartyId, setCounterpartyId] = useState<number | ''>(
    txn.transfer_candidate_bank_account_id ?? '',
  );
  const [originalId, setOriginalId] = useState<number | ''>(
    txn.refund_candidate_bank_transaction_id ?? '',
  );
  const refundMode = mode === 'refund' && inflow;
  const refundCandidates = useRefundCandidates(txn.id, refundMode).data ?? [];

  // Capitalizing is only ever a payment out, so the lookups stay idle on a
  // deposit and on anything already reviewed.
  const capitalizeMode = mode === 'capitalize' && !inflow;
  const capitalize = useCapitalizeTransaction();
  const openAssets = useFixedAssets('in_progress').data ?? [];
  const assetClasses = useAssetClasses().data ?? [];
  const [assetId, setAssetId] = useState<number | typeof NEW_ASSET_OPTION | ''>('');
  const [newAssetName, setNewAssetName] = useState('');
  const [newAssetClass, setNewAssetClass] = useState<AssetClass>('equipment_7');
  const [component, setComponent] = useState('');
  const [showFiles, setShowFiles] = useState(focused);
  const [viewing, setViewing] = useState<ViewableFile | null>(null);
  const rowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (focused) {
      rowRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [focused]);

  // Transfer mode only makes sense when there's another account to move to/from.
  const transferMode = mode === 'transfer' && otherAccounts.length > 0;

  const amountLabel = useMemo(
    () => (inflow ? `+${dollars(txn.amount_cents)}` : dollars(txn.amount_cents)),
    [inflow, txn.amount_cents],
  );

  async function onPost() {
    if (accountId === '') {
      toast.error('Pick a category first');
      return;
    }
    try {
      await post.mutateAsync({ id: txn.id, account_id: Number(accountId) });
      toast.success('Posted to the ledger');
    } catch (e) {
      toast.error(errorMessage(e) ?? "Couldn't post this transaction");
    }
  }

  // The primary select doubles as the mode switch: choosing "Transfer between
  // accounts" or "Refund of a purchase" flips modes; a real category flips back.
  function onPrimaryChange(value: string) {
    if (value === TRANSFER_OPTION) {
      setMode('transfer');
      return;
    }
    if (value === REFUND_OPTION) {
      setMode('refund');
      return;
    }
    if (value === CAPITALIZE_OPTION) {
      setMode('capitalize');
      return;
    }
    setMode('category');
    setAccountId(value === '' ? '' : Number(value));
  }

  async function onRefund() {
    if (originalId === '') {
      toast.error('Pick the original purchase this refunds');
      return;
    }
    try {
      await refund.mutateAsync({ id: txn.id, original_bank_transaction_id: Number(originalId) });
      toast.success('Refund matched to the original purchase');
    } catch (e) {
      toast.error(errorMessage(e) ?? "Couldn't record this refund");
    }
  }

  async function onTransfer() {
    if (counterpartyId === '') {
      toast.error('Choose which account this transferred to or from');
      return;
    }
    try {
      await transfer.mutateAsync({
        id: txn.id,
        counterparty_bank_account_id: Number(counterpartyId),
      });
      toast.success('Transfer recorded');
    } catch (e) {
      toast.error(errorMessage(e) ?? "Couldn't record this transfer");
    }
  }

  async function onCapitalize() {
    if (assetId === '') {
      toast.error('Pick which asset this is part of');
      return;
    }
    if (assetId === NEW_ASSET_OPTION && !newAssetName.trim()) {
      toast.error('Name the new asset');
      return;
    }
    if (!component.trim()) {
      toast.error('Say what this charge was for');
      return;
    }
    try {
      await capitalize.mutateAsync({
        id: txn.id,
        component: component.trim(),
        ...(assetId === NEW_ASSET_OPTION
          ? { name: newAssetName.trim(), asset_class: newAssetClass }
          : { fixed_asset_id: assetId }),
      });
      toast.success('Capitalized, not expensed');
    } catch (e) {
      toast.error(errorMessage(e) ?? "Couldn't capitalize this charge");
    }
  }

  async function onIgnore() {
    try {
      await ignore.mutateAsync(txn.id);
      toast.success('Transaction ignored');
    } catch {
      toast.error("Couldn't ignore this transaction");
    }
  }

  async function onUnpost() {
    try {
      await unpost.mutateAsync(txn.id);
      toast.success('Reverted — back to review');
    } catch {
      toast.error("Couldn't unpost this transaction");
    }
  }

  const fileCount = txn.attachments.length;

  return (
    <div
      ref={rowRef}
      className={
        focused
          ? 'rounded-md ring-2 ring-[var(--color-primary)] ring-offset-2 ring-offset-[var(--color-surface)]'
          : undefined
      }
    >
      <DataListRow>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="truncate font-medium text-foreground">
              {txn.merchant_name ?? txn.name ?? 'Transaction'}
            </span>
            {txn.pending ? <Badge tone="neutral">Pending</Badge> : null}
            {txn.status === 'posted' ? (
              txn.is_transfer ? (
                <Badge tone="info">Transfer</Badge>
              ) : txn.is_refund ? (
                <Badge tone="info">Refund</Badge>
              ) : (
                <Badge tone="success">Posted</Badge>
              )
            ) : null}
            {txn.status === 'transferred' ? <Badge tone="info">Transferred</Badge> : null}
            {txn.status === 'ignored' ? <Badge tone="neutral">Ignored</Badge> : null}
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {formatDateFull(txn.txn_date)}
            {txn.bank_account_name ? ` · ${txn.bank_account_name}` : ''}
          </div>
          {txn.status === 'unreviewed' && txn.transfer_candidate_bank_account_id != null ? (
            <div className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-[var(--color-primary)]">
              <ArrowLeftRight className="size-3" />
              Looks like a transfer
              {txn.transfer_candidate_label ? ` with ${txn.transfer_candidate_label}` : ''}
            </div>
          ) : null}
          {txn.status === 'unreviewed' &&
          txn.transfer_candidate_bank_account_id == null &&
          txn.refund_candidate_bank_transaction_id != null ? (
            <div className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-[var(--color-primary)]">
              <Undo2 className="size-3" />
              Looks like a refund
              {txn.refund_candidate_label ? ` of ${txn.refund_candidate_label}` : ''}
            </div>
          ) : null}
          {txn.linked_invoices.length > 0 ? (
            <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px]">
              <span className="text-muted-foreground">Settles</span>
              {txn.linked_invoices.map((inv) => (
                <button
                  key={inv.invoice_id}
                  type="button"
                  onClick={() => navigate(`/clients/${inv.client_id}?tab=invoices`)}
                  className="font-medium text-[var(--color-primary)] tabular-nums hover:underline"
                >
                  {inv.invoice_number}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <AttachmentChip
          label={
            fileCount === 1
              ? txn.attachments[0].file_name
              : fileCount > 1
                ? `${fileCount} files`
                : 'Attach'
          }
          title={fileCount === 1 ? txn.attachments[0].file_name : undefined}
          onClick={() => setShowFiles((v) => !v)}
        />

        <div
          className={`w-28 shrink-0 text-right font-medium tabular-nums ${
            inflow ? 'text-emerald-600 dark:text-emerald-400' : 'text-foreground'
          }`}
        >
          {amountLabel}
        </div>

        {txn.status === 'unreviewed' ? (
          <div
            className={
              capitalizeMode
                ? 'flex w-[24rem] shrink-0 items-start gap-2'
                : 'flex w-[24rem] shrink-0 items-center gap-2'
            }
          >
            {refundMode ? (
              <Select
                value={originalId}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === CATEGORY_OPTION) {
                    setMode('category');
                    return;
                  }
                  setOriginalId(v === '' ? '' : Number(v));
                }}
                className="flex-1"
              >
                <option value="">
                  {refundCandidates.length > 0 ? 'Refund of…' : 'No matching purchase found'}
                </option>
                {refundCandidates.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label} · {dollars(c.amount_cents)} · {formatDateFull(c.txn_date)}
                  </option>
                ))}
                <option value={CATEGORY_OPTION}>← Pick a category instead</option>
              </Select>
            ) : transferMode ? (
              <Select
                value={counterpartyId}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === CATEGORY_OPTION) {
                    setMode('category');
                    return;
                  }
                  setCounterpartyId(v === '' ? '' : Number(v));
                }}
                className="flex-1"
              >
                <option value="">Transfer to/from…</option>
                {otherAccounts.map((a) => (
                  <option key={a.id} value={a.id} disabled={!a.mapped}>
                    {a.label}
                    {a.mapped ? '' : ' (map first)'}
                  </option>
                ))}
                <option value={CATEGORY_OPTION}>← Pick a category instead</option>
              </Select>
            ) : capitalizeMode ? (
              <div className="flex flex-1 flex-col gap-1.5">
                <Select
                  value={assetId}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === CATEGORY_OPTION) {
                      setMode('category');
                      return;
                    }
                    setAssetId(
                      v === '' ? '' : v === NEW_ASSET_OPTION ? NEW_ASSET_OPTION : Number(v),
                    );
                  }}
                >
                  <option value="">Part of which asset…</option>
                  {openAssets.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name} · {dollars(a.capitalized_cents)} so far
                    </option>
                  ))}
                  <option value={NEW_ASSET_OPTION}>+ Start a new asset</option>
                  <option value={CATEGORY_OPTION}>← Pick a category instead</option>
                </Select>
                {assetId === NEW_ASSET_OPTION ? (
                  <>
                    <Input
                      placeholder="Asset name, e.g. Container office"
                      value={newAssetName}
                      onChange={(e) => setNewAssetName(e.target.value)}
                    />
                    <Select
                      value={newAssetClass}
                      onChange={(e) => setNewAssetClass(e.target.value as AssetClass)}
                    >
                      {assetClasses.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label} · {c.recovery_years}-year
                        </option>
                      ))}
                    </Select>
                  </>
                ) : null}
                {assetId !== '' ? (
                  <Input
                    placeholder="What was this charge for? e.g. Gravel pad"
                    value={component}
                    onChange={(e) => setComponent(e.target.value)}
                  />
                ) : null}
              </div>
            ) : (
              <Select
                value={accountId}
                onChange={(e) => onPrimaryChange(e.target.value)}
                className="flex-1"
              >
                <option value="">Pick a category…</option>
                {inflow ? <option value={REFUND_OPTION}>↩ Refund of a purchase</option> : null}
                {otherAccounts.length > 0 ? (
                  <option value={TRANSFER_OPTION}>⇄ Transfer between accounts</option>
                ) : null}
                {/* The third thing a payment out can be. §263 does not let you
                    deduct what you spent acquiring something with a life beyond
                    this year, and this queue is where that call gets made. */}
                {!inflow ? (
                  <option value={CAPITALIZE_OPTION}>▣ Part of a capital asset</option>
                ) : null}
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.code} · {a.name}
                  </option>
                ))}
              </Select>
            )}
            {refundMode ? (
              <Button onClick={onRefund} disabled={refund.isPending}>
                <Undo2 className="size-3.5" /> Refund
              </Button>
            ) : capitalizeMode ? (
              <Button onClick={onCapitalize} disabled={capitalize.isPending}>
                <Boxes className="size-3.5" /> Capitalize
              </Button>
            ) : transferMode ? (
              <Button onClick={onTransfer} disabled={transfer.isPending}>
                <ArrowLeftRight className="size-3.5" /> Transfer
              </Button>
            ) : (
              <Button onClick={onPost} disabled={post.isPending}>
                <Check className="size-3.5" /> Post
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={onIgnore}
              disabled={ignore.isPending}
              aria-label="Ignore"
            >
              <Ban className="size-3.5" />
            </Button>
          </div>
        ) : txn.status === 'posted' ? (
          <div className="flex w-[24rem] shrink-0 justify-end">
            <Button variant="secondary" onClick={onUnpost} disabled={unpost.isPending}>
              <RotateCcw className="size-3.5" />{' '}
              {txn.is_transfer ? 'Undo transfer' : txn.is_refund ? 'Undo refund' : 'Unpost'}
            </Button>
          </div>
        ) : txn.status === 'transferred' ? (
          <div className="flex w-[24rem] shrink-0 justify-end">
            <Button variant="secondary" onClick={onUnpost} disabled={unpost.isPending}>
              <RotateCcw className="size-3.5" /> Undo transfer
            </Button>
          </div>
        ) : (
          <div className="flex w-[24rem] shrink-0 justify-end">
            <Button variant="ghost" onClick={onUnpost} disabled={unpost.isPending}>
              <RotateCcw className="size-3.5" /> Restore
            </Button>
          </div>
        )}
      </DataListRow>

      {showFiles ? <TransactionAttachments txn={txn} onView={setViewing} /> : null}

      <AttachmentViewer file={viewing} onClose={() => setViewing(null)} />
    </div>
  );
}

/**
 * Files attached directly to a bank transaction — receipts, check photos,
 * warranties, invoices. Stored on the transaction so they persist across
 * unpost/repost and show up on whichever journal entry it's posted to.
 */
function TransactionAttachments({
  txn,
  onView,
}: {
  txn: BankTransactionData;
  onView: (file: ViewableFile) => void;
}) {
  const upload = useUploadTransactionAttachment();
  const remove = useDeleteTransactionAttachment();
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file after an error
    if (!file) return;
    try {
      await upload.mutateAsync({ txnId: txn.id, file });
      toast.success('File attached');
    } catch (err) {
      toast.error('Upload failed', errorMessage(err) ?? 'Could not upload that file.');
    }
  }

  async function onDelete(mediaId: number) {
    try {
      await remove.mutateAsync({ txnId: txn.id, mediaId });
      toast.success('File removed');
    } catch {
      toast.error("Couldn't remove that file");
    }
  }

  return (
    <div className="border-t border-border/50 bg-muted/20 px-5 py-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          <Paperclip className="size-3" />
          Files
        </span>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={upload.isPending}
          className="inline-flex items-center gap-1 text-xs font-medium text-[var(--color-primary)] hover:underline disabled:opacity-50"
        >
          {upload.isPending ? (
            <Loader2 className="size-3 animate-spin" />
          ) : (
            <Upload className="size-3" />
          )}
          {upload.isPending ? 'Uploading…' : 'Attach file'}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*,application/pdf"
          className="hidden"
          onChange={onPick}
        />
      </div>

      {txn.attachments.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          No files attached. Add a receipt, check photo, warranty, or invoice (PDF or image) to back
          this transaction up.
        </p>
      ) : (
        <ul className="space-y-1">
          {txn.attachments.map((r) => (
            <li
              key={r.id}
              className="flex items-center gap-2 rounded-md border border-border/50 bg-card px-2.5 py-1.5"
            >
              {r.is_image ? (
                <button type="button" onClick={() => onView(r)} className="shrink-0">
                  <img src={r.url} alt={r.file_name} className="size-8 rounded object-cover" />
                </button>
              ) : (
                <FileText className="size-5 shrink-0 text-muted-foreground" />
              )}
              <button
                type="button"
                onClick={() => onView(r)}
                className="min-w-0 flex-1 truncate text-left text-sm text-foreground hover:underline"
              >
                {r.file_name}
              </button>
              <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                {formatBytes(r.file_size)}
              </span>
              <button
                type="button"
                onClick={() => onDelete(r.id)}
                disabled={remove.isPending}
                aria-label={`Remove ${r.file_name}`}
                className="flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-destructive disabled:opacity-50"
              >
                <Trash2 className="size-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function errorMessage(e: unknown): string | null {
  if (typeof e === 'object' && e !== null && 'response' in e) {
    const resp = (e as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } })
      .response;
    const firstError = resp?.data?.errors ? Object.values(resp.data.errors)[0]?.[0] : undefined;
    return firstError ?? resp?.data?.message ?? null;
  }
  return null;
}
